import { Refresh } from "@mui/icons-material";
import { Box, Divider, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import { ReactElement, useCallback, useState } from "react";

import { FlexBox } from "~frontend/components/FlexBox";
import { LinkButton } from "~frontend/components/Link";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import { palette } from "~frontend/theme";

import { CampaignContactFieldChip } from "./CampaignContactFieldChip";
import { TemplatePreview } from "./TemplatePreview";
import { CreateOrEditGifFormValues } from "./types";

function DoubleInputLabel({
  label,
  secondaryLabel,
}: {
  label: string;
  secondaryLabel?: string;
}): ReactElement {
  return (
    <label>
      <FlexBox
        justifyContent="space-between"
        sx={{
          marginLeft: "2px",
        }}
      >
        <Typography
          sx={{ fontWeight: 600, fontSize: 12, color: palette.darkGray }}
        >
          {label}
        </Typography>

        <Typography variant="h4Caps">{secondaryLabel}</Typography>
      </FlexBox>
    </label>
  );
}

export function CustomizeTextFields(): ReactElement {
  const form = useFormikContext<CreateOrEditGifFormValues>();

  const [previewCustomFields, setPreviewCustomFields] = useState(
    form.values.customFields
  );
  const refreshPreview = useCallback(
    () => setPreviewCustomFields(form.values.customFields),
    [setPreviewCustomFields, form]
  );

  return (
    <>
      <FlexBox
        flexDirection="column"
        alignItems="center"
        gap="24px"
        sx={{ marginBottom: "24px" }}
      >
        <TemplatePreview
          template={form.values.template!}
          customFields={previewCustomFields}
          customColors={form.values.customColors}
          logoUrl={form.values.logoUrl}
        />
        <FlexBox alignItems="center" gap="8px" onClick={refreshPreview}>
          <Refresh />
          <LinkButton>Update Preview</LinkButton>
        </FlexBox>
      </FlexBox>
      <Divider sx={{ borderColor: palette.midGray, marginBottom: "24px" }} />
      <FormikTextField key="name" name="name" label="Your GIF name" fullWidth />
      {form.values.template!.requiredCustomFields.map(customField => (
        <>
          <FormikTextField
            key={`customFields.${customField.name}`}
            name={`customFields.${customField.name}`}
            label={customField.friendlyName}
            placeholder={customField.placeholder}
            fullWidth
            slots={{
              inputLabel: () => (
                <DoubleInputLabel
                  label={`${customField.friendlyName}`}
                  secondaryLabel={`${customField.charLimit} character limit`}
                />
              ),
            }}
          />
        </>
      ))}
      {form.values.template?.requiredContactFields.length ? (
        <>
          <Typography sx={{ paddingY: "16px" }} variant="body1">
            Your campaign contact upload will need to include the following
            custom fields to map to. You can enter fallback text for each field
            here, that will be used if the field is missing, contains special
            characters that can not be rendered in the gif, or exceeds the
            character limit. If a custom field is missing or incompatible, and
            no fallback is specified, that contact will be skipped in the send
          </Typography>
          <FlexBox gap="8px" flexDirection="column">
            {form.values.template.requiredContactFields.map(contactField => (
              <Box>
                <CampaignContactFieldChip
                  key={contactField.name}
                  fieldName={contactField.name}
                />
                <FormikTextField
                  key={`customFields.${contactField.name}`}
                  name={`customFields.${contactField.name}`}
                  label={contactField.name}
                  placeholder={contactField.placeholder}
                  fullWidth
                  slots={{
                    inputLabel: () => (
                      <DoubleInputLabel
                        label={`${contactField.name} Fallback`}
                        secondaryLabel={`${contactField.charLimit} character limit`}
                      />
                    ),
                  }}
                  sx={{ paddingTop: 0 }}
                />
              </Box>
            ))}
          </FlexBox>
        </>
      ) : null}
    </>
  );
}
